import { useEffect, useState } from "react";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useMvl from "../../data/Mvl/useMvl";
import useNotification from "../../components/shared/Notifications/useNotifications";

export default function MvlPage() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { count, importXlsx } = useMvl();

  const [file, setFile] = useState<File | null>(null);
  const [truncate, setTruncate] = useState(true);

  useEffect(() => {
    setInfo({
      name: "Amazon MVL",
      desc: "Import and manage the Amazon Automotive GB Master Vehicle List (Make/Model → KType)",
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async () => {
    if (!file) {
      addNotification({ primaryText: "Select an xlsx file first", variant: "warn" });
      return;
    }

    try {
      const result = await importXlsx.mutateAsync({ file, truncate });
      addNotification({
        primaryText: result.message || `Imported ${result.data?.imported ?? 0} rows`,
        variant: "success",
      });
      setFile(null);
    } catch (err: any) {
      addNotification({
        primaryText: err?.message || "MVL import failed",
        variant: "error",
      });
    }
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900">Current list</h2>
        <p className="mt-2 text-sm text-gray-600">
          {count.isLoading
            ? "Loading count…"
            : `${count.data?.toLocaleString() ?? 0} vehicle rows loaded`}
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Import Amazon Automotive GB MVL</h2>
          <p className="mt-1 text-sm text-gray-600">
            Upload the Amazon Automotive GB CAR MVL xlsx (Data sheet with Make, Model, VariantName, Ktype, etc.).
            A full replace (truncate) is recommended for each new Amazon release.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">XLSX file</label>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="mt-1 block w-full text-sm text-gray-700"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && (
            <p className="mt-1 text-xs text-gray-500">
              Selected: {file.name} ({Math.round(file.size / 1024 / 1024 * 10) / 10} MB)
            </p>
          )}
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={truncate}
            onChange={(e) => setTruncate(e.target.checked)}
          />
          <span>
            <span className="font-medium">Replace existing rows</span>
            <span className="block text-xs text-gray-500">
              Clears the current MVL table before importing. Leave checked for a full Amazon refresh.
            </span>
          </span>
        </label>

        <button
          type="button"
          onClick={handleImport}
          disabled={!file || importXlsx.isPending}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-60"
        >
          {importXlsx.isPending ? "Importing…" : "Import MVL"}
        </button>
      </div>
    </div>
  );
}
